import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { SignalFieldBaseDirective } from '../signal-field-base.directive';

/**
 * Low-level number stepper for signal forms — the +/- variant of
 * `NumberFieldComponent`, sharing the generic base and therefore the same
 * `number | null` value and validation contract:
 *
 * ```html
 * <pace-core-number-stepper-field [formField]="signalForm.distance" [step]="5" />
 * ```
 *
 * Like the number field, the bounds arrive through the `FormUiControl` contract
 * from the schema's `min()`/`max()` rules, so the buttons know when to stop without
 * being told the bounds twice.
 *
 * Two deliberate corrections to the reactive-forms `NumberStepperComponent`:
 *
 * - stepping away from an empty field starts from 0, so a step of 5 gives 5. The
 *   legacy `NumberModel.inc()/dec()` treats *any* falsy value as empty, which makes
 *   incrementing a stored 0 jump to 1 instead of to the step.
 * - the guards are named for what they do. In the legacy component `isIncDisabled()`
 *   guards the decrement button and vice versa, with the template compensating.
 */
@Component({
    selector: 'pace-number-stepper-field',
    templateUrl: './number-stepper-field.component.html',
    styleUrls: ['./number-stepper-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberStepperFieldComponent extends SignalFieldBaseDirective<number | null> {
    /** the edited value; `null` while the field is empty */
    public readonly value = model<number | null>(null);

    // --- `FormUiControl` inputs, all bound automatically by `[formField]` -----

    public readonly min = input<number | undefined>(undefined);
    public readonly max = input<number | undefined>(undefined);

    // --- own inputs ----------------------------------------------------------

    /** amount a button press adds or subtracts */
    public readonly step = input(1);

    /** the value a button press would produce, from 0 when the field is empty */
    private readonly incremented = computed(() => (this.value() ?? 0) + this.step());
    private readonly decremented = computed(() => (this.value() ?? 0) - this.step());

    public readonly editable = computed(() => !this.readonly() && !this.disabled());

    /**
     * A step that would cross a bound is refused rather than clamped to it, which is
     * how the reactive-forms stepper behaves.
     */
    public readonly canIncrement = computed(() => this.editable() && this.incremented() <= (this.max() ?? Infinity));
    public readonly canDecrement = computed(() => this.editable() && this.decremented() >= (this.min() ?? -Infinity));

    public increment(): void {
        if (this.canIncrement()) {
            this.setValue(this.incremented());
        }
    }

    public decrement(): void {
        if (this.canDecrement()) {
            this.setValue(this.decremented());
        }
    }

    /**
     * A number input reports an empty string both when it is cleared and while the
     * typed text is not a valid number, and `valueAsNumber` is NaN in both cases —
     * either way the field has no value, so it publishes `null` and lets the
     * schema's `required` rule speak.
     *
     * @param event the native input event
     */
    public onInput(event: Event): void {
        const parsed = (<HTMLInputElement>event.target).valueAsNumber;
        this.value.set(Number.isNaN(parsed) ? null : parsed);
    }

    /**
     * A button press is an interaction, even though it produces no blur.
     *
     * @param next the stepped value to publish
     */
    private setValue(next: number): void {
        this.value.set(next);
        this.touched.set(true);
    }
}
